import { Component, booleanAttribute, CUSTOM_ELEMENTS_SCHEMA, Input, OnChanges, SimpleChange } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidationMessageService } from '../../../services/validation-message.service';
import { CommonModule } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'lf-control-messages',
    styleUrls: ['./control-messages.scss'],
    templateUrl: './control-messages.component.html',
    standalone: true,
    imports: [
        CommonModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ControlMessagesComponent implements OnChanges
{
    @Input() control: AbstractControl;
    @Input() saved: boolean = false;
    @Input() errorsWhenPristine: boolean = false;
    @Input() displayError: boolean;
    @Input() successMessage: string = 'Updated successfully.';
    @Input() prefix = '';
    @Input() useConsolidatedMessage = false;
    @Input({ transform: booleanAttribute }) hint = false;

    errorMessage: string = null;

    private controlSubscription: Subscription | null = null;

    constructor(private _validationMessageService: ValidationMessageService) {
    }

    ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
        // This change event fires for the first time when the control is passed into the directive.
        if (changes.hasOwnProperty('control')) {
            const c: AbstractControl = changes['control'].currentValue;

            // Follow the control the host passes now, not just the first one: a host that rebuilds its form
            // (create-owner after a registration loads) hands over a new control, and the message must track it.
            this.controlSubscription?.unsubscribe();
            this.controlSubscription = c
                ? merge(c.valueChanges, c.statusChanges).pipe(untilDestroyed(this)).subscribe(() => this.changeWatcher())
                : null;

            this.changeWatcher();
        }

        if (changes.hasOwnProperty('displayError')) {
            this.changeWatcher();
        }

        if (changes.hasOwnProperty('prefix')) {
            this.changeWatcher();
        }
    }

    public changeWatcher() {
        const messages: any[] = [];
        if (this.control && this.control.errors) {
            if (this.useConsolidatedMessage && (this.errorsWhenPristine || this.control.touched || !this.control.pristine)) {
                // Use for fields that have multiple errors to correct (password-hinter)
                messages.push('Please correct errors for this field.');
            } else {
                for (const p in this.control.errors) {
                    if (this.control.errors[p]) {
                        if (p === 'mask') {
                            continue;
                        }
                        if (this.control.errors.hasOwnProperty(p) && (this.errorsWhenPristine || this.control.touched || !this.control.pristine)) {
                            // Get the appropriate error message.
                            const msg: any = this._validationMessageService.getGenericErrorMessage(p, this.control.errors[p]);

                            if (!msg) {
                                if (this.control.errors[p] !== true && !(this.control.errors.hasOwnProperty('invalidPhoneFormat'))) {
                                    messages.push(this.prefix
                                            ? this.control.errors[p].replace('This field', this.prefix)
                                            : this.control.errors[p]);
                                }
                            } else {
                                if (!msg.requiredMask) {
                                    messages.push(this.prefix ? msg.replace('This field', this.prefix) : msg);
                                }
                            }
                        }
                    }
                }
            }
        }

        if (messages.length > 0) {
            this.errorMessage = messages.join('<br />');
        } else {
            this.errorMessage = null;
        }
    }
}
